/**
 * Items.
 *
 * Until now everything in the inventory was a block. Seeds, grain and every
 * tool to come are not: they have an icon and a name but nothing to place.
 *
 * **Block items keep the block's own id.** That is deliberate - inventories
 * are written into save files as bare numbers, so making the two id spaces
 * agree means every save written before items existed still loads. Items that
 * are not blocks start well above the block range.
 */

import {
  BLOCKS, blockDef, BlockId, BEET_CROP_BASE, MAZANKA_SLAB, GRANITE_SLAB,
  isCrossRender,
} from "./registry.js";
import type { ToolKind } from "./tools.js";
import { shafted } from "./tilePixels.js";
import type { ArmourDef } from "../entity/armour.js";
import { pick, type Lang, type Phrase } from "../text/lang.js";

export type ItemId = number;

/** Where non-block items begin. Blocks will not reach this for a long time. */
export const FIRST_NON_BLOCK_ITEM = 200;

export const enum PlainItemId {
  /**
   * Wheat, whether it is going into the ground or into bread. There was a
   * separate "зерно" item at 201 for a while; it was the same thing under
   * another name, and 201 stays retired so no future item inherits it and
   * turns old saves into a bag of something else.
   */
  WheatSeeds = 200,
  Stick = 202,
  WoodenHoe = 203,
  OakDoor = 204,
  AcaciaDoor = 205,
  Stove = 206,
  StoveLimed = 207,
  OakShutter = 208,
  AcaciaShutter = 209,
  Bed = 210,
  ClayBucket = 211,
  WaterBucket = 212,
  Vat = 213,
  FlaxSeeds = 214,
  FlaxStalks = 215,
  Fibre = 216,
  Linen = 217,
  SpinningWheel = 218,
  Rushnyk = 219,
  WoodenPickaxe = 220,
  WoodenShovel = 221,
  WoodenSabre = 222,
  Bow = 223,
  Arrow = 224,

  /*
   * Raw materials out of the ground. Nothing is made from any of them yet -
   * they are dug, carried and stacked, and the furnace that turns ore into
   * metal is its own piece of work.
   */
  Coal = 225,
  IronOre = 226,
  ManganeseOre = 227,
  Graphite = 228,
  CrudeOil = 229,
  Peat = 230,
  Ilmenite = 231,
  Rutile = 232,

  /** Скриня. What it holds lives in world/containers.ts, not in the item. */
  Chest = 233,

  /** Out of the fire: what the ores become. */
  IronIngot = 234,
  Manganese = 235,

  /** Мазана стіна. Four block states, one thing to carry. */
  MazankaWall = 236,

  /**
   * The iron kit: the first thing worth going into the fire for.
   *
   * The pick is here rather than in the wooden run above because it is the
   * same tool in better metal, and grouping by tier is how a player thinks
   * about them - "what can I make now that I can smelt?"
   */
  IronPickaxe = 237,
  Helm = 238,
  Mail = 239,
  Bracers = 240,
  Boots = 241,

  /**
   * Food. Raw off the animal, and what the піч makes of it.
   *
   * There is no hunger in this game, so eating heals instead - which is the
   * only thing food could usefully do here, and it makes a пічка worth
   * keeping lit between raids rather than only while smelting.
   */
  RawPork = 242,
  Pork = 243,
  RawBeef = 244,
  Beef = 245,

  /** Карась, out of the water and out of the fire. */
  RawKaras = 246,
  Karas = 247,
  /** Вудка. A stick, a line of лляне волокно, and patience. */
  FishingRod = 248,
  /** The шабля the wooden one was always training for. */
  IronSabre = 249,
  /** Смола, out of a pine trunk and out of nothing else. */
  Resin = 250,

  /** Курятина, raw and roasted. */
  RawChicken = 251,
  Chicken = 252,

  /*
   * Letting a creature out.
   *
   * Numbered from 264 because 256-263 are the flowing-water blocks, and an
   * item id and a block id are the same number space - an item at 256 would
   * be water with a different name on it. Creative-only: nothing makes these
   * and nothing generates them.
   */
  SpawnPig = 264,
  SpawnTur = 265,
  SpawnKaras = 266,
  SpawnOrc = 267,
  SpawnSelianyn = 268,
  SpawnSelianka = 269,
  SpawnKurka = 276,
  SpawnSobaka = 278,
  SpawnKit = 279,

  /**
   * Шматочок золота, off an orc.
   *
   * The first source of gold in the game, and it closes a gap: позолочена
   * бляха had no recipe at all and could only be had from the комора or by
   * stripping a dome. Now the domes are paid for by fighting.
   */
  GoldBit = 270,
  /** Шабля орка-ватажка. Off its body, and off nothing else. */
  LeaderSabre = 271,
  /** Вогняний снаряд: a stone in linen, soaked in смола. */
  Firebolt = 272,

  /*
   * The drone, and what runs it.
   *
   * Modern, in a game about козаки - deliberately, and only here. This map is
   * a mini-game about burning a кремль; when there is a full world it will
   * keep a consistent century and things like this will live in mods. Said
   * out loud so nobody later mistakes it for drift.
   */
  Drone = 273,
  DroneFlat = 274,
  Battery = 275,

  /**
   * Кресало: the steel and the flint, and the only way to start a fire by
   * hand.
   *
   * 277 because 276 is the курка's egg-and-spawn item. There has been fire
   * in this game since the кремль - a вогняний снаряд throws it - but no way
   * at all to light one without a siege engine, which is a strange gap in a
   * world where the thing you most want at night is a fire.
   */
  Kresalo = 277,

  /**
   * Човен: a довбанка, hollowed out of one log.
   *
   * 280, after the two комора eggs at 278 and 279. Not a block: a boat put
   * down is a thing floating on water, and a block of boat would be
   * something you walk on top of.
   */
  Choven = 280,

  /**
   * Кістка, out of anything that had one.
   *
   * What a dog wants, and the reason a собака can be won over by somebody
   * with no сало to spare. It is not food: you cannot eat it and it mends
   * nothing.
   */
  Bone = 281,

  /**
   * Шкура вовкулаки, off the thing itself and off nothing else.
   *
   * The вовкулака left nothing at all - "you do not hunt one, you get past
   * one" - and that is still true of meeting one in the dark. What changes
   * is that killing one is worth something afterwards: a hide burns like
   * лляне полотно and makes the same смолоскип, which is the difference
   * between a night spent hiding and a night spent lighting a cave.
   */
  VovkulakaHide = 282,
  /** Щит: boards, an iron rim and a cross. What a скелет-страж carries. */
  WoodenShield = 283,

  /**
   * Золотий злиток.
   *
   * The one thing in the game that comes out of the ground already made.
   * There is no ore for it and no recipe: gold arrives as шматочки off an
   * orc, and as a bar in a chest somebody buried and never came back for.
   */
  GoldIngot = 284,

  /**
   * Луска дракона, and the four things made of it.
   *
   * Off a Змій Горинич and off nothing else. One dragon yields exactly a
   * set - that is what Pavlo asked for and it is what the numbers below add
   * up to, so killing one is a whole suit of armour rather than a quarter of
   * one and a long wait for the next.
   */
  DragonScale = 285,
  ScaleHelm = 286,
  ScaleMail = 287,
  ScaleBracers = 288,
  ScaleBoots = 289,

  /** Гранітна стіна, the same shape as the мазана one in dressed stone. */
  GraniteWall = 290,

  /** And the same two materials lying down: half a block each. */
  MazankaSlab = 291,
  GraniteSlab = 292,

  /**
   * Пульт: the handset the drone is flown from.
   *
   * "Коли виходиш з дрона він зчезає - зроби щоб був пульт в який треба було б
   * заходити щоб ним управляти." Before this, stepping out of the drone
   * dropped it out of the sky: there was no way back to something already in
   * the air. Now leaving is leaving the controls, the machine stays up
   * burning its battery, and this is what you pick up to look through it
   * again.
   */
  Remote = 293,

  /**
   * Буряк, and what it is sown from.
   *
   * "Додай буряк і нехай в них росте теж." Unlike wheat, the seed and the
   * crop are two different things here and they have to be: you sow a seed
   * and you eat a root, and one item for both would mean eating next year's
   * sowing without noticing.
   */
  BeetSeeds = 294,
  Beet = 295,

  /**
   * Сокира, in wood and in iron.
   *
   * "Додай сокиру, дерев'яну та залізну, буде швидше рубати всі дерев'яні
   * блоки." The tool the game was missing: every other material had one and
   * wood had a шабля standing in for it, which is a blade doing a wedge's
   * job. With blows counted, the difference is the whole point of it.
   */
  WoodenAxe = 296,
  IronAxe = 297,

  /**
   * Адамантієве кайло та шабля.
   *
   * "Зібраний адамантій можна використати для створення кайла та шаблі."
   * The crystal itself is a block - you carry it, you can set it down again -
   * so only the two tools are items.
   */
  AdamantPickaxe = 298,
  AdamantSabre = 299,

  /*
   * 300 to 308 are the lying logs and the beet, which are blocks. The item
   * space ran out at 299 and this is where it carries on.
   */

  /**
   * Порох and гума: what a танк and its shells are made of.
   *
   * "Оскільки немає поки де брати порох - нехай він буде захований в землі у
   * скринях." Neither is made: both come out of chests buried on «Сучасна
   * війна», which is also why neither has a recipe and why the check that
   * walks the crafting tree from an empty bag has to be told so.
   */
  Gunpowder = 309,
  Rubber = 310,
  /** A shell: порох and залізо, and the only thing that hurts a танк. */
  TankShell = 311,

  /*
   * 312 to 316 are the прапори, which are blocks.
   */

  /**
   * Танк. Iron, oil and rubber, and only on «Сучасна війна».
   *
   * Carried as an item and put down as a machine, the way a човен is: what
   * you place is not a block but a thing with a driver's seat in it.
   */
  Tank = 317,
}

/** Items that no longer exist, mapped to what replaced them. */
export const RETIRED_ITEMS: Readonly<Record<number, number>> = {
  201: PlainItemId.WheatSeeds,
  // "Залізна шабля 2": the iron sabre with Meshy's model unshrunk, kept for
  // a day to compare against. It showed where the quality went, and went.
  318: PlainItemId.IronSabre,
};

/** What an item id from an older save should become. */
export function migrateItemId(id: number): number {
  return RETIRED_ITEMS[id] ?? id;
}

/**
 * What right-clicking with an item does, when it does something other than
 * place a block.
 *
 * A union rather than a boolean, because the axe, the spade and the sickle are
 * all coming and each needs its own verb.
 */
export type ItemUse =
  | { kind: "till" }
  /** Sow onto farmland. `crop` is the block the first stage places. */
  | { kind: "plant"; crop: number }
  /** Loose an arrow, spending one from the bag. */
  | { kind: "shoot" }
  /** Throw the line in, or strike if it is already out. */
  | { kind: "fish" }
  /** Eat it, mending `heals` points of сила and spending one. */
  | { kind: "eat"; heals: number }
  /** Put a living thing down. See core/entity/spawn.ts. */
  | { kind: "spawn"; creature: string }
  /**
   * Send it up, and look through it.
   *
   * The one use that takes the camera off the player. See
   * core/entity/drone.ts for what happens after.
   */
  | { kind: "drone" }
  /** Back to the controls of a drone already in the air. */
  | { kind: "remote" }
  /** Strike a spark into whatever you are pointing at, and hope it takes. */
  | { kind: "strike" }
  /** Put a човен on the water in front of you and leave it floating there. */
  | { kind: "launch" };

/**
 * Will a dog take this from your hand?
 *
 * Meat, and a кістка. Written here rather than at the place the dog is fed
 * because it is a fact about the item: a собака wants what a собака wants,
 * and the page that happens to be holding it should not be the thing that
 * decides.
 */
export function tamesDogs(id: number): boolean {
  if (id === PlainItemId.Bone) return true;
  const use = itemUse(id as ItemId);
  return use?.kind === "eat";
}

export interface ItemDef {
  readonly id: ItemId;
  readonly name: string;
  /** Ukrainian display name. The original, and the fallback for nothing. */
  readonly label: string;
  /** The same name in English. Required, for the reason BlockDef.en gives. */
  readonly en: string;
  /** Texture tile used for the inventory icon. */
  readonly tile: number;
  /**
   * A second picture, shown only when the 18+ switch is on.
   *
   * One item with one id, one name and one damage - only the drawing changes.
   * The clean tile is the default, so a caller that has not been told the
   * tone shows the polite one, which is the same way round the written lines
   * fail.
   */
  readonly adultTile?: number;
  /**
   * How this is carried, when the standard size does not suit it.
   *
   * The picture is drawn to read at sixteen pixels in a slot; held at arm's
   * length it is a different problem. A шабля at the size that suits a lump
   * of ore is a dagger, and a lump of ore at the size that suits a шабля is a
   * boulder. There is no one number, so this is where a particular thing says
   * what it needs - which is data about the item and belongs beside its name,
   * not in the renderer.
   */
  readonly held?: {
    /** Multiplier on the standard held size. */
    readonly scale?: number;
    /** Extra turn in the face of the picture. Positive swings the point left. */
    readonly turn?: number;
    /**
     * Extra tilt away from the eye, in radians. Positive lifts the point.
     *
     * Its own knob because it does a different thing: `turn` moves the point
     * around the screen, `tilt` leans the whole thing back, which is what
     * takes a blade held nearly upright - and reading as a knife held up -
     * and lays it out along its own length.
     */
    readonly tilt?: number;
  };
  /**
   * What this becomes once it has been used up.
   *
   * A relationship rather than a flag, because two things need it and would
   * otherwise disagree: the game, when it puts the spent thing back in the
   * world, and the check that everything a player can hold is obtainable -
   * a spent form has no recipe and never will, and saying so here is better
   * than a list of exceptions by name.
   */
  readonly spendsTo?: ItemId;
  /**
   * The block this places when used, if it places one at all.
   *
   * A number rather than a `BlockId`: the enum is the blocks with names of
   * their own, and a плита or a стіна is a block the registry builds in a
   * range - `MAZANKA_SLAB` and the rest are just as much blocks and are not
   * in it.
   */
  readonly places?: number;
  /** What it does on right-click instead of placing. */
  readonly use?: ItemUse;
  /**
   * What this counts as when breaking a block.
   *
   * Separate from `use`, because a tool acts on the left button and `use` on
   * the right. The hoe is the only thing that is both, and it is a hoe for
   * tilling rather than a tool for digging - which is why these are two
   * fields and not one.
   */
  readonly tool?: ToolKind;
  /**
   * Where this is worn and what it turns aside. Absent means it is not armour.
   *
   * Worth keeping beside `tool` and `damage` rather than in a table of its
   * own: an item is one row here, and a second table keyed by id is a second
   * place to forget to update.
   */
  readonly armour?: ArmourDef;
  /**
   * What this does to a living thing when swung, in points.
   *
   * Absent means a bare fist, which is UNARMED_DAMAGE. A tool is better than a
   * fist and worse than a weapon: you *can* see off an orc with a spade, and
   * you would rather not.
   */
  readonly damage?: number;
  /** Tools stack alone; materials stack normally. */
  readonly maxStack?: number;
  /**
   * One line saying what the thing is for, shown under its name on hover.
   *
   * Only where it adds something. Repeating "камінь - це камінь" under every
   * block would train the player to stop reading tooltips.
   */
  readonly hint?: Phrase;
}

/** Items that are not blocks. */
export const PLAIN_ITEMS: readonly ItemDef[] = [
  {
    id: PlainItemId.WheatSeeds, name: "wheat_seeds", label: "пшениця", en: "wheat seed",
    tile: 23, use: { kind: "plant", crop: 22 /* BlockId.WheatCrop0 */ },
    hint: { uk: "Сіяти в ріллю. ПКМ згори", en: "Sow in ploughed ground. Right-click from above" },
  },
  {
    id: PlainItemId.BeetSeeds, name: "beet_seeds", label: "насіння буряка",
    en: "beetroot seed", tile: 185,
    use: { kind: "plant", crop: BEET_CROP_BASE },
    hint: { uk: "Сіяти в ріллю. ПКМ згори",
      en: "Sow in ploughed ground. Right-click from above" },
  },
  {
    // Boiled, roasted or raw in a борщ. Worth more than a raw one of anything
    // because it is the thing a garden is *for*.
    id: PlainItemId.Beet, name: "beet", label: "буряк", en: "beetroot",
    tile: 184, use: { kind: "eat", heals: 3 },
    hint: { uk: "ПКМ — з'їсти. Лікує 3", en: "Right-click to eat. Heals 3" },
  },
  {
    id: PlainItemId.Stick, name: "stick", label: "палиця", en: "stick", tile: 26,
    hint: { uk: "Держак для інструментів", en: "A handle for tools" },
  },
  {
    // Tile 31 is the iron head, painted and waiting for iron to be smeltable.
    // This one is deliberately the cruder tool.
    id: PlainItemId.WoodenHoe, name: "wooden_hoe", label: "дерев'яна сапка", en: "wooden hoe",
    tile: 27, use: { kind: "till" }, maxStack: 1, damage: 2, held: { scale: 1.25 },
    hint: { uk: "ПКМ згори по землі — зорати", en: "Right-click earth from above to plough it" },
  },
  {
    id: PlainItemId.WoodenPickaxe, name: "wooden_pickaxe", label: "дерев'яне кайло", en: "wooden pickaxe",
    tile: 68, tool: "pickaxe", maxStack: 1, damage: 2, held: { scale: 1.25 },
    hint: { uk: "Без нього камінь не взяти", en: "Without one you cannot take stone" },
  },
  {
    id: PlainItemId.WoodenAxe, name: "wooden_axe", label: "дерев'яна сокира",
    en: "wooden axe",
    tile: 186, tool: "axe", maxStack: 1, damage: 3, held: { scale: 1.3 },
    hint: {
      uk: "Рубає дерево втричі швидше за руки",
      en: "Chops wood three times faster than bare hands",
    },
  },
  {
    id: PlainItemId.WoodenShovel, name: "wooden_shovel", label: "дерев'яна лопата", en: "wooden shovel",
    tile: 69, tool: "shovel", maxStack: 1, damage: 2, held: { scale: 1.25 },
    hint: { uk: "Копати глину з дна озера", en: "Digs clay from a lake bed" },
  },
  {
    // Wooden because iron is not smeltable yet - and because a training sabre
    // is a real thing, which is how you learned before you were trusted with
    // steel. The steel one comes with the forge.
    id: PlainItemId.WoodenSabre, name: "wooden_sabre", label: "дерев'яна шабля", en: "wooden sabre",
    tile: 70, tool: "sabre", maxStack: 1, damage: 4, held: { scale: 1.5, tilt: 0.34 },
    hint: { uk: "Тренувальна. Справжня буде із заліза", en: "For practice. The real one will be iron" },
  },
  {
    id: PlainItemId.Bow, name: "bow", label: "лук", en: "bow",
    tile: 71, use: { kind: "shoot" }, maxStack: 1, held: { scale: 1.3 },
    hint: { uk: "ПКМ — пустити стрілу", en: "Right-click to loose an arrow" },
  },
  {
    id: PlainItemId.Arrow, name: "arrow", label: "стріла", en: "arrow",
    tile: 72, hint: { uk: "Набій для лука", en: "Ammunition for the bow" },
  },
  {
    // Placed by main.ts rather than by `places`, because a chest turns to face
    // whoever sets it down - the same as a stove.
    id: PlainItemId.Chest, name: "chest", label: "скриня", en: "chest", tile: 90,
    hint: { uk: "ПКМ — відчинити. Тримає 27 стосів", en: "Right-click to open. Holds 27 stacks" },
  },
  {
    // Placed by main.ts rather than by `places`, because it needs the same
    // "not inside the player, not inside a villager" checks the stove has.
    id: PlainItemId.MazankaWall, name: "mazanka_wall", label: "мазана стіна", en: "daub wall",
    tile: 95, hint: { uk: "Сама тягнеться до сусідніх стін і блоків", en: "Reaches out to walls and blocks beside it" },
  },
  {
    id: PlainItemId.GraniteWall, name: "granite_wall", label: "гранітна стіна",
    en: "granite wall", tile: 174,
    hint: { uk: "Те саме, але з граніту", en: "The same, in granite" },
  },
  /*
   * The плити say what block they put down, which is how anything else here
   * says it.
   *
   * They did not, and were placed by a table in `main.ts` instead - so the
   * комора could not tell the item from the block it places and shelved both:
   * two «мазані плити», two «гранітні плити», and nothing to choose between
   * them. "Прибери дублікати з комори."
   */
  {
    id: PlainItemId.MazankaSlab, name: "mazanka_slab", label: "мазана плита",
    en: "daub slab", tile: 175, places: MAZANKA_SLAB,
    hint: { uk: "Пів блока заввишки. Сходинка, а не стіна",
      en: "Half a block tall. A step, not a wall" },
  },
  {
    id: PlainItemId.GraniteSlab, name: "granite_slab", label: "гранітна плита",
    en: "granite slab", tile: 176, places: GRANITE_SLAB,
    hint: { uk: "Пів блока заввишки, з граніту",
      en: "Half a block tall, in granite" },
  },
  {
    // Tile 97 is the same drawing as the wooden pick in iron, which is the
    // point: better metal, same tool. It digs nothing the wooden one cannot -
    // there is no mining speed yet, so the honest difference is that it hits
    // harder, and the hint says exactly that rather than implying more.
    id: PlainItemId.IronPickaxe, name: "iron_pickaxe", label: "залізне кайло", en: "iron pickaxe",
    tile: 97, tool: "pickaxe", maxStack: 1, damage: 5, held: { scale: 1.25 },
    hint: { uk: "Б'є вдвічі важче за дерев'яне", en: "Hits twice as hard as the wooden one" },
  },
  {
    // The same drawing in steel, the way the two picks are one drawing: a
    // сокира is its wedge, and a different outline would say it was a
    // different tool rather than a better one.
    id: PlainItemId.IronAxe, name: "iron_axe", label: "залізна сокира",
    en: "iron axe",
    tile: 187, tool: "axe", maxStack: 1, damage: 6, held: { scale: 1.3 },
    hint: {
      uk: "Криця. Колода з двох ударів",
      en: "Steel. A log in two blows",
    },
  },
  {
    id: PlainItemId.Gunpowder, name: "gunpowder", label: "порох",
    en: "gunpowder",
    tile: 195, maxStack: 64,
    hint: {
      uk: "Зі скринь, закопаних у землю. На снаряди",
      en: "Out of chests buried in the ground. For shells",
    },
  },
  {
    id: PlainItemId.Rubber, name: "rubber", label: "гума", en: "rubber",
    tile: 196, maxStack: 64,
    hint: {
      uk: "Зі скринь, закопаних у землю. На гусениці",
      en: "Out of chests buried in the ground. For tracks",
    },
  },
  {
    id: PlainItemId.TankShell, name: "tank_shell", label: "снаряд",
    en: "shell",
    tile: 197, maxStack: 16,
    hint: {
      uk: "Для танка. Порох і залізо",
      en: "For the tank. Gunpowder and iron",
    },
  },
  {
    id: PlainItemId.Tank, name: "tank", label: "танк", en: "tank",
    tile: 198, maxStack: 1, held: { scale: 1.4 },
    hint: {
      uk: "ПКМ — поставити. Далі сядь у нього й керуй",
      en: "Right-click to set it down, then get in and drive",
    },
  },
  {
    id: PlainItemId.AdamantPickaxe, name: "adamant_pickaxe",
    label: "адамантієве кайло", en: "adamantium pickaxe",
    tile: 190, tool: "pickaxe", maxStack: 1, damage: 7, held: { scale: 1.25 },
    hint: {
      uk: "Єдине, чим можна зламати прапор",
      en: "The only thing that takes a flag down",
    },
  },
  {
    id: PlainItemId.AdamantSabre, name: "adamant_sabre",
    label: "адамантієва шабля", en: "adamantium sabre",
    tile: 191, tool: "sabre", maxStack: 1, damage: 11,
    held: { scale: 1.5, tilt: 0.34 },
    hint: {
      uk: "Криця ламається об неї",
      en: "Steel breaks against it",
    },
  },
  {
    id: PlainItemId.WoodenShield, name: "wooden_shield", label: "щит",
    en: "shield",
    tile: 167, maxStack: 1, held: { scale: 1.6, tilt: 0 },
    // Worn on the off arm, in the slot of its own. No points: what a shield
    // does is a question for another day, and nought is the honest answer
    // until it has one.
    armour: { slot: "shield", points: 0 },
    hint: {
      uk: "Дошки, окуття й хрест. Зі скелета в замку",
      en: "Boards, an iron rim and a cross. Off a guard in the dead king's castle",
    },
  },
  {
    // The real one. The wooden sabre's own hint has promised this since the
    // day it was added: "Тренувальна. Справжня буде із заліза."
    id: PlainItemId.IronSabre, name: "iron_sabre", label: "шабля", en: "sabre",
    tile: 112, tool: "sabre", maxStack: 1, damage: 7, held: { scale: 1.5, tilt: 0.34 },
    hint: { uk: "Криця. Найкраще, чим тут можна вдарити", en: "Steel. The hardest thing here to strike with" },
  },
  {
    id: PlainItemId.Helm, name: "iron_helm", label: "шишак", en: "helm",
    tile: 98, maxStack: 1, armour: { slot: "head", points: 2 },
    hint: { uk: "На голову. Кований, із шпилем", en: "For the head. Forged, with a spike" },
  },
  {
    id: PlainItemId.Mail, name: "iron_mail", label: "кольчуга", en: "mail shirt",
    tile: 99, maxStack: 1, armour: { slot: "body", points: 3 },
    hint: { uk: "На тулуб. Найбільше боронить", en: "For the body. Protects the most" },
  },
  {
    id: PlainItemId.Bracers, name: "iron_bracers", label: "наручі", en: "bracers",
    tile: 100, maxStack: 1, armour: { slot: "arms", points: 1 },
    hint: { uk: "На руки. Щоб було чим прийняти удар", en: "For the arms, to take a blow on" },
  },
  {
    id: PlainItemId.Boots, name: "iron_boots", label: "залізні чоботи", en: "iron boots",
    tile: 101, maxStack: 1, armour: { slot: "feet", points: 2 },
    hint: { uk: "На ноги", en: "For the legs" },
  },
  {
    // Raw meat heals a little, and is worth eating only if you are in trouble
    // with no fire to hand. The point of it is to be cooked.
    id: PlainItemId.RawPork, name: "raw_pork", label: "сире сало", en: "raw pork fat",
    tile: 105, use: { kind: "eat", heals: 1 },
    hint: { uk: "Краще засмажити в печі", en: "Better roasted in a stove" },
  },
  {
    id: PlainItemId.Pork, name: "pork", label: "смажене сало", en: "roast pork fat",
    tile: 106, use: { kind: "eat", heals: 4 },
    hint: { uk: "ПКМ — з'їсти. Лікує 4", en: "Right-click to eat. Heals 4" },
  },
  {
    id: PlainItemId.RawChicken, name: "raw_chicken", label: "сира курятина",
    en: "raw chicken", tile: 151, use: { kind: "eat", heals: 1 },
    hint: { uk: "Краще засмажити в печі", en: "Better roasted in a stove" },
  },
  {
    id: PlainItemId.Chicken, name: "chicken", label: "смажена курятина",
    en: "roast chicken", tile: 152, use: { kind: "eat", heals: 4 },
    hint: { uk: "ПКМ — з'їсти. Лікує 4", en: "Right-click to eat. Heals 4" },
  },
  {
    id: PlainItemId.RawBeef, name: "raw_beef", label: "сира яловичина", en: "raw beef",
    tile: 107, use: { kind: "eat", heals: 1 },
    hint: { uk: "Краще засмажити в печі", en: "Better roasted in a stove" },
  },
  {
    id: PlainItemId.Beef, name: "beef", label: "смажена яловичина", en: "roast beef",
    tile: 108, use: { kind: "eat", heals: 5 },
    hint: { uk: "ПКМ — з'їсти. Лікує 5", en: "Right-click to eat. Heals 5" },
  },
  {
    id: PlainItemId.Resin, name: "resin", label: "смола", en: "resin",
    tile: 119, hint: { uk: "З соснової колоди. Горить і клеїть", en: "From a pine log. It burns and it sticks" },
  },
  {
    /*
     * Кресало.
     *
     * Held rather than placed, and it wears out at nothing: a striker is a
     * bent bar of steel and a stone, and neither is used up by making a
     * spark. What limits it is what will catch - straw and boards will,
     * stone and earth never will, which is `catchesFire` and not this.
     */
    id: PlainItemId.Kresalo, name: "kresalo", label: "кресало",
    en: "fire striker", tile: 154,
    use: { kind: "strike" },
  },
  {
    /*
     * Човен.
     *
     * Carried like a plank and put down on water. It is the one thing in the
     * bag whose use needs water in front of you rather than ground, which is
     * said in the hint because there is nowhere else to say it.
     */
    id: PlainItemId.Choven, name: "choven", label: "човен",
    en: "boat", tile: 158, maxStack: 1,
    use: { kind: "launch" },
    hint: { uk: "ПКМ по воді — спустити на воду", en: "Right-click water to launch" },
  },
  {
    id: PlainItemId.Bone, name: "bone", label: "кістка", en: "bone",
    tile: 159,
    hint: { uk: "ПКМ по собаці — приручити", en: "Right-click a dog to tame it" },
  },
  {
    id: PlainItemId.VovkulakaHide, name: "vovkulaka_hide",
    label: "шкура вовкулаки", en: "werewolf hide", tile: 160,
  },
  {
    id: PlainItemId.RawKaras, name: "raw_karas", label: "карась", en: "crucian",
    tile: 109, use: { kind: "eat", heals: 1 },
    hint: { uk: "Сирим не дуже. У піч його", en: "Not much raw. Into the stove with it" },
  },
  {
    id: PlainItemId.Karas, name: "karas", label: "смажений карась", en: "roast crucian",
    tile: 110, use: { kind: "eat", heals: 3 },
    hint: { uk: "ПКМ — з'їсти. Лікує 3", en: "Right-click to eat. Heals 3" },
  },
  {
    id: PlainItemId.FishingRod, name: "fishing_rod", label: "вудка", en: "fishing rod",
    tile: 111, use: { kind: "fish" }, maxStack: 1, held: { scale: 1.35 },
    hint: { uk: "ПКМ по воді — закинути. Клюне — тягни", en: "Right-click water to cast. When it bites, pull" },
  },
  {
    id: PlainItemId.IronIngot, name: "iron_ingot", label: "залізний злиток", en: "iron ingot",
    tile: 93, hint: { uk: "З печі. Криця для інструментів", en: "From the stove. Steel for tools" },
  },
  {
    id: PlainItemId.Manganese, name: "manganese", label: "марганець", en: "manganese",
    tile: 94, hint: { uk: "З печі. Із ним криця стає твердою", en: "From the stove. It makes steel hard" },
  },
  {
    id: PlainItemId.Coal, name: "coal", label: "вугілля", en: "coal", tile: 82,
    hint: { uk: "Горить довго. Донбас", en: "Burns a long time. From the Donbas" },
  },
  {
    id: PlainItemId.IronOre, name: "iron_ore_raw", label: "сира залізна руда",
    en: "raw iron", tile: 83,
    hint: { uk: "Криворізька. Треба плавити", en: "From Kryvyi Rih. Needs smelting" },
  },
  {
    id: PlainItemId.ManganeseOre, name: "manganese_ore_raw",
    label: "сира марганцева руда", en: "raw manganese", tile: 84,
    hint: { uk: "Нікопольська. Треба плавити", en: "From Nikopol. Needs smelting" },
  },
  {
    id: PlainItemId.Graphite, name: "graphite", label: "графіт", en: "graphite", tile: 85,
    hint: { uk: "Завалівський. Мажеться і не горить", en: "From Zavallia. It smudges and will not burn" },
  },
  {
    id: PlainItemId.CrudeOil, name: "crude_oil", label: "сира нафта", en: "crude oil", tile: 86,
    hint: { uk: "Прикарпатська. Поки нема що з нею робити", en: "From Prykarpattia. Nothing to do with it yet" },
  },
  {
    id: PlainItemId.Peat, name: "peat", label: "торф", en: "peat", tile: 87,
    // Dug out of a bog and put back down again, like any other earth. Without
    // this the комора had two «торфи»: the block and what it drops.
    places: BlockId.Peat,
    hint: { uk: "З болота. Сушать і палять", en: "From the bog. Dried and burnt" },
  },
  {
    id: PlainItemId.Ilmenite, name: "ilmenite", label: "ільменіт", en: "ilmenite", tile: 88,
    hint: { uk: "Титан із піску. Іршанський", en: "Titanium from sand. From Irshansk" },
  },
  {
    id: PlainItemId.Rutile, name: "rutile", label: "рутил", en: "rutile", tile: 89,
    hint: { uk: "Титан із піску, рідший за ільменіт", en: "Titanium from sand, rarer than ilmenite" },
  },
  {
    // Doors place two blocks and are not ordinary block items, so they carry
    // no `places` - main.ts handles them specially.
    id: PlainItemId.OakDoor, name: "oak_door", label: "дубові двері", en: "oak door",
    tile: 32, hint: { uk: "Два блоки заввишки. ПКМ — відчинити", en: "Two blocks tall. Right-click to open" },
  },
  {
    id: PlainItemId.AcaciaDoor, name: "acacia_door", label: "двері з акації", en: "acacia door",
    tile: 34, hint: { uk: "Два блоки заввишки. ПКМ — відчинити", en: "Two blocks tall. Right-click to open" },
  },
  {
    // Stoves turn to face whoever puts them down, so main.ts places them.
    id: PlainItemId.Stove, name: "stove", label: "піч", en: "stove",
    tile: 41, hint: { uk: "Топка дивиться на тебе", en: "The firebox looks at you" },
  },
  {
    id: PlainItemId.StoveLimed, name: "stove_limed", label: "побілена піч", en: "limewashed stove",
    tile: 44, hint: { uk: "Вибілена вапном і мальована, як стіни хати", en: "Limewashed and painted, like the walls of a хата" },
  },
  {
    id: PlainItemId.OakShutter, name: "oak_shutter", label: "дубові віконниці", en: "oak shutters",
    tile: 46, hint: { uk: "Вішається на стіну. ПКМ — відчинити", en: "Hangs on a wall. Right-click to open" },
  },
  {
    id: PlainItemId.AcaciaShutter, name: "acacia_shutter",
    label: "віконниці з акації", en: "acacia shutters",
    tile: 47, hint: { uk: "Вішається на стіну. ПКМ — відчинити", en: "Hangs on a wall. Right-click to open" },
  },
  {
    // Two blocks long, laid down rather than stacked.
    id: PlainItemId.Bed, name: "bed", label: "ліжко", en: "bed",
    tile: 49, hint: { uk: "ПКМ — спати вночі. Тут ти й прокинешся", en: "Right-click to sleep at night. You wake here" },
  },
  {
    id: PlainItemId.ClayBucket, name: "clay_bucket", label: "глиняне відро", en: "clay pail",
    tile: 55, hint: { uk: "ПКМ по воді — набрати", en: "Right-click water to fill it" },
  },
  {
    id: PlainItemId.WaterBucket, name: "water_bucket", label: "відро з водою", en: "pail of water",
    tile: 56, hint: { uk: "ПКМ по чану — налити", en: "Right-click a vat to pour it in" },
  },
  {
    id: PlainItemId.Vat, name: "vat", label: "глиняний чан", en: "clay vat",
    tile: 53, places: BlockId.Vat, hint: { uk: "Налий води — і можна мочити льон", en: "Pour water in and you can ret flax" },
  },
  {
    id: PlainItemId.FlaxSeeds, name: "flax_seeds", label: "насіння льону", en: "flax seed",
    tile: 57, use: { kind: "plant", crop: BlockId.FlaxCrop0 },
    hint: { uk: "Сіяти в ріллю. ПКМ згори", en: "Sow in ploughed ground. Right-click from above" },
  },
  {
    id: PlainItemId.FlaxStalks, name: "flax_stalks", label: "стебла льону", en: "flax stalks",
    tile: 61, hint: { uk: "Вимочити в чані — вийде волокно", en: "Ret it in a vat to get fibre" },
  },
  {
    id: PlainItemId.Fibre, name: "fibre", label: "волокно", en: "fibre",
    tile: 62, hint: { uk: "Прясти на прядці", en: "Spin it on a wheel" },
  },
  {
    id: PlainItemId.Linen, name: "linen", label: "полотно", en: "linen",
    tile: 63, hint: { uk: "Лляне полотно", en: "Linen cloth" },
  },
  {
    id: PlainItemId.SpinningWheel, name: "spinning_wheel", label: "прядка", en: "spinning wheel",
    tile: 64, places: BlockId.SpinningWheel, hint: { uk: "Прясти волокно на полотно", en: "Spins fibre into cloth" },
  },
  /*
   * The drone.
   *
   * Right-click puts it in the air and your eyes in it. Your body stays
   * exactly where you left it, with the world still running round it - that
   * is the price, and it is what makes scouting the кремль a decision rather
   * than a free look.
   */
  {
    id: PlainItemId.Drone, name: "drone", label: "дрон", en: "drone",
    tile: 145, use: { kind: "drone" }, maxStack: 1, spendsTo: PlainItemId.DroneFlat,
    held: { scale: 1.4 },
    hint: { uk: "ПКМ — пустити й дивитись з нього. Літає 5 хвилин",
      en: "Right-click to send it up and look through it. Five minutes of flight" },
  },
  {
    id: PlainItemId.DroneFlat, name: "drone_flat", label: "розряджений дрон",
    en: "flat drone", tile: 146, maxStack: 1,
    held: { scale: 1.4 },
    hint: { uk: "Батарея сіла. Постав нову", en: "The battery is flat. Put a new one in" },
  },
  /*
   * The пульт.
   *
   * Not spent and not stacked: it is a tool, and the one you own is the one
   * you keep. Right-click takes the controls of whatever is up - there is
   * only ever one drone, so it needs no way to choose between them.
   */
  {
    id: PlainItemId.Remote, name: "remote", label: "пульт", en: "remote",
    tile: 177, use: { kind: "remote" }, maxStack: 1,
    held: { scale: 1.2 },
    hint: { uk: "ПКМ — сісти за керування дроном, що вже в повітрі",
      en: "Right-click to take the controls of a drone already up" },
  },
  {
    id: PlainItemId.Battery, name: "battery", label: "батарея", en: "battery",
    tile: 147, maxStack: 16,
    hint: { uk: "Марганець із Нікополя. П'ять хвилин польоту",
      en: "Manganese from Nikopol. Five minutes of flight" },
  },

  {
    id: PlainItemId.Rushnyk, name: "rushnyk", label: "рушник", en: "rushnyk",
    tile: 65, hint: { uk: "Вішається на стіну", en: "Hangs on a wall" },
  },

  /*
   * Letting a creature out. Creative-only: no recipe makes these.
   *
   * Written out rather than generated from core/entity/spawn.ts, which is
   * where the creatures themselves are described. Generating them from there
   * made this file import that one and that one import this - a cycle
   * TypeScript accepts happily and the browser does not, and the game died on
   * load with `Cannot read properties of undefined`. The direction has to be
   * one way: items are the lower layer, creatures build on them.
   *
   * A test compares the two tables field by field, so they cannot drift.
   */
  {
    id: PlainItemId.LeaderSabre, name: "leader_sabre", label: "шабля ватажка",
    en: "leader's sabre", tile: 139, adultTile: 140,
    maxStack: 1, damage: 9, held: { scale: 1.65, tilt: 0.34 },
    hint: { uk: "З ватажка. Б'є важче за будь-що інше",
      en: "Off the leader. Hits harder than anything else" },
  },
  {
    id: PlainItemId.Firebolt, name: "firebolt", label: "вогняний снаряд",
    en: "firebolt", tile: 144, maxStack: 16,
    hint: { uk: "Заряди в требушет. ПКМ по ньому",
      en: "Load it into a trebuchet. Right-click one" },
  },
  {
    id: PlainItemId.GoldIngot, name: "gold_ingot", label: "золотий злиток",
    en: "gold ingot", tile: 168,
    hint: { uk: "Зі скарбу. Більше його ніде не взяти",
      en: "Out of buried treasure. There is nowhere else to get one" },
  },
  {
    id: PlainItemId.DragonScale, name: "dragon_scale", label: "луска дракона",
    en: "dragon scale", tile: 169,
    hint: { uk: "Зі Змія Горинича. З неї кують обладунок",
      en: "Off a Zmii Horynych. Armour is forged from it" },
  },
  {
    id: PlainItemId.ScaleHelm, name: "scale_helm", label: "шолом із луски",
    en: "scale helm", tile: 170, maxStack: 1,
    armour: { slot: "head", points: 3 },
    hint: { uk: "На голову. Міцніший за шишак", en: "For the head. Harder than a helm" },
  },
  {
    id: PlainItemId.ScaleMail, name: "scale_mail", label: "лускатий панцир",
    en: "scale mail", tile: 171, maxStack: 1,
    armour: { slot: "body", points: 4 },
    hint: { uk: "На тулуб. Найміцніше, що є", en: "For the body. The hardest there is" },
  },
  {
    id: PlainItemId.ScaleBracers, name: "scale_bracers", label: "лускаті наручі",
    en: "scale bracers", tile: 172, maxStack: 1,
    armour: { slot: "arms", points: 2 },
    hint: { uk: "На руки", en: "For the arms" },
  },
  {
    id: PlainItemId.ScaleBoots, name: "scale_boots", label: "лускаті чоботи",
    en: "scale boots", tile: 173, maxStack: 1,
    armour: { slot: "feet", points: 3 },
    hint: { uk: "На ноги", en: "For the legs" },
  },
  {
    id: PlainItemId.GoldBit, name: "gold_bit", label: "шматочок золота",
    en: "bit of gold", tile: 136,
    hint: { uk: "З орка. Купол із цього й роблять",
      en: "Off an orc. What a dome is covered in" },
  },
  {
    id: PlainItemId.SpawnPig, name: "spawn_pig", label: "свиня", en: "pig",
    tile: 130, maxStack: 16, use: { kind: "spawn", creature: "pig" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    id: PlainItemId.SpawnTur, name: "spawn_tur", label: "тур", en: "aurochs",
    tile: 131, maxStack: 16, use: { kind: "spawn", creature: "tur" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    id: PlainItemId.SpawnKurka, name: "spawn_kurka", label: "курка", en: "hen",
    tile: 153, maxStack: 16, use: { kind: "spawn", creature: "kurka" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    id: PlainItemId.SpawnSobaka, name: "spawn_sobaka", label: "собака",
    en: "dog", tile: 156, maxStack: 16,
    use: { kind: "spawn", creature: "sobaka" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    id: PlainItemId.SpawnKit, name: "spawn_kit", label: "кіт",
    en: "cat", tile: 157, maxStack: 16,
    use: { kind: "spawn", creature: "kit" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    // «Живий», because there is a карась you eat as well, and a bag with two
    // of the same word in it is a bag nobody can read.
    id: PlainItemId.SpawnKaras, name: "spawn_karas", label: "живий карась",
    en: "live crucian",
    tile: 132, maxStack: 16, use: { kind: "spawn", creature: "karas" },
    hint: { uk: "ПКМ по воді — випустити", en: "Right-click water to let it out" },
  },
  {
    id: PlainItemId.SpawnOrc, name: "spawn_orc", label: "орк", en: "orc",
    tile: 133, maxStack: 16, use: { kind: "spawn", creature: "orc" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    id: PlainItemId.SpawnSelianyn, name: "spawn_selianyn", label: "селянин",
    en: "villager man",
    tile: 134, maxStack: 16, use: { kind: "spawn", creature: "selianyn" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
  {
    id: PlainItemId.SpawnSelianka, name: "spawn_selianka", label: "селянка",
    en: "villager woman",
    tile: 135, maxStack: 16, use: { kind: "spawn", creature: "selianka" },
    hint: { uk: "ПКМ по землі — випустити", en: "Right-click ground to let it out" },
  },
];

/** Which shutter blocks an item places, by wood. */
export const SHUTTER_ITEMS: readonly ItemId[] = [
  PlainItemId.OakShutter, PlainItemId.AcaciaShutter,
];

/** The wood a shutter item is made of, or -1 if it is not one. */
export function shutterItemWood(id: ItemId): number {
  return SHUTTER_ITEMS.indexOf(id);
}

/** Which stove blocks an item places. Index matches StoveFinish. */
export const STOVE_ITEMS: readonly ItemId[] = [
  PlainItemId.Stove, PlainItemId.StoveLimed,
];

/** The finish a stove item places, or -1 if it is not a stove. */
export function stoveItemFinish(id: ItemId): number {
  return STOVE_ITEMS.indexOf(id);
}

/** Which door blocks an item places, by wood. Index matches DoorWood. */
export const DOOR_ITEMS: readonly ItemId[] = [
  PlainItemId.OakDoor, PlainItemId.AcaciaDoor,
];

/** The wood a door item is made of, or -1 if it is not a door. */
export function doorItemWood(id: ItemId): number {
  return DOOR_ITEMS.indexOf(id);
}

/** Hints for a few blocks, where what they are for is not obvious. */
const BLOCK_HINTS: Readonly<Record<number, Phrase>> = {
  26: { uk: "Видно наскрізь", en: "You can see straight through" },                        // Glass
  27: { uk: "Вікно хати. Рама з дуба", en: "A хата window. Oak frame" },               // OakWindow
  28: { uk: "Вікно хати. Рама з акації", en: "A хата window. Acacia frame" },             // AcaciaWindow
  7: { uk: "Стріха, і разом з глиною — мазанка", en: "Thatch, and with clay it makes daub" },   // Thatch
  6: { uk: "Цегла, і разом з соломою — мазанка", en: "Brick, and with straw it makes daub" },   // Clay
  9: { uk: "Стіна хати", en: "The wall of a хата" },                            // Mazanka
  19: { uk: "Зламай — впаде насіння", en: "Break it and seed falls" },               // WildWheat
  20: { uk: "Суха. Пшениця тут не росте", en: "Dry. Wheat will not grow here" },           // Farmland
  21: { uk: "Волога. Тут росте пшениця", en: "Wet. Wheat grows here" },            // FarmlandWet
  24: { uk: "Стигла. Час збирати", en: "Ripe. Time to harvest" },                  // WheatCrop2
};

/** Sparse lookup by id. Dense enough at this size, and one load to read. */
const BY_ID: (ItemDef | undefined)[] = [];

for (const block of BLOCKS) {
  if (block.id === 0) continue;
  /*
   * A block item keeps the block's own id, which is only possible below where
   * plain items start - so a block numbered above that range cannot be an item
   * and must not become one.
   *
   * Flowing water is the first such block, and it is the right shape for the
   * rule: it is not a thing you could ever hold, it is a state that water is
   * in. Making an item of it would put eight indistinguishable "вода" entries
   * in the recipe book, each colliding with an unrelated item's number.
   */
  if (block.id >= FIRST_NON_BLOCK_ITEM) continue;
  BY_ID[block.id] = {
    id: block.id,
    name: block.name,
    label: block.label,
    en: block.en,
    tile: block.faces.top,
    places: block.id,
  };
}
for (const item of PLAIN_ITEMS) BY_ID[item.id] = item;

/** Every item, blocks first. Used by the recipe book and tests. */
export const ITEMS: readonly ItemDef[] =
  BY_ID.filter((i): i is ItemDef => i !== undefined);

const UNKNOWN: ItemDef = {
  id: 0, name: "unknown", label: "?", en: "?", tile: 0,
};

export function itemDef(id: ItemId): ItemDef {
  return BY_ID[id] ?? UNKNOWN;
}

/** The block this item places, or 0 if it places nothing. */
export function placedBlock(id: ItemId): BlockId {
  return (itemDef(id).places ?? 0) as BlockId;
}

export function isPlaceable(id: ItemId): boolean {
  return itemDef(id).places !== undefined;
}

/**
 * The name for the HUD and tooltips, in the language in force.
 *
 * Ukrainian by default rather than by lookup: this game is Ukrainian first,
 * and a caller that has not been taught about language gets the original
 * rather than a blank.
 */
export function itemLabel(id: ItemId, lang: Lang = "uk"): string {
  const def = itemDef(id);
  return lang === "en" ? def.en : def.label;
}

/** One line about what the item is for, or undefined when the name says it. */
export function itemHint(id: ItemId, lang: Lang = "uk"): string | undefined {
  const hint = itemDef(id).hint ?? BLOCK_HINTS[id];
  return hint === undefined ? undefined : pick(hint, lang);
}

/** Icon tile. Blocks show their top face, which is the recognisable one. */
/**
 * Whether a thing is a picture rather than a block.
 *
 * A sabre in the hand or on the ground is its drawing, extruded; a block
 * of stone is a cube of its faces. A plant is a picture too - in the
 * world it is two crossed ones - and drawn as a cube it is a box with a
 * beet painted on every side. "Зроби, щоб воно виглядало як маленьке
 * дерево, по прикладу шаблі."
 */
export function drawnFlat(id: ItemId): boolean {
  return id >= FIRST_NON_BLOCK_ITEM || (id !== 0 && isCrossRender(id));
}

export function itemTile(id: ItemId, tone: "family" | "adult" = "family"): number {
  // A block shows its top face, unless its shape is the point - a stair
  // wears stone, and a square of stone says "a block of stone".
  if (id !== 0 && id < FIRST_NON_BLOCK_ITEM) {
    const def = blockDef(id);
    return def.icon ?? def.faces.top;
  }
  const def = itemDef(id);
  return tone === "adult" ? def.adultTile ?? def.tile : def.tile;
}

/**
 * How big this is carried, and how it is turned in the hand.
 *
 * One place to ask, with the defaults filled in, so nothing that draws a held
 * thing has to know which items said something and which did not.
 */
export function heldStyle(
  id: ItemId,
): { scale: number; turn: number; tilt: number; shafted: boolean } {
  const held = itemDef(id).held;
  const onShaft = shafted(itemTile(id));
  // A shafted tool is drawn head up, handle down; whoever draws it in a
  // hand turns it to suit the hand. See `shafted`.
  return { scale: held?.scale ?? 1, turn: held?.turn ?? 0, tilt: held?.tilt ?? 0, shafted: onShaft };
}

/** What a used-up one leaves behind, if anything. */
export function spentForm(id: ItemId): ItemId | undefined {
  return itemDef(id).spendsTo;
}

/** What this item does on right-click, if anything. */
export function itemUse(id: ItemId): ItemUse | undefined {
  return itemDef(id).use;
}

/** What a bare fist is worth. */
export const UNARMED_DAMAGE = 1;

/** What swinging this does to a living thing. */
export function itemDamage(id: ItemId): number {
  return itemDef(id).damage ?? UNARMED_DAMAGE;
}

/** What this counts as when breaking a block, if it counts as anything. */
/** Where this is worn and what it is worth, or undefined if it is not armour. */
export function itemArmour(id: ItemId): ArmourDef | undefined {
  return itemDef(id).armour;
}

export function itemTool(id: ItemId): ToolKind | undefined {
  return itemDef(id).tool;
}

/** How many of this item fit in one slot. Tools stack alone. */
export function itemMaxStack(id: ItemId): number {
  return itemDef(id).maxStack ?? 64;
}
